import { useEffect, useState } from 'react';
import ApiDataBase from 'server';
import { templateText } from './templateTexts';

// One request per page session, shared by the detail panel and the wizard. A
// failed request is dropped so the next mount retries.
let referentialRequest = null;
let loadedReferential = null;

const sortByOrder = (response) =>
  (Array.isArray(response) ? response : [])
    .filter((item) => typeof item?.besttField === 'string' && item.besttField)
    .sort(
      (a, b) =>
        (a.order ?? Number.MAX_SAFE_INTEGER) -
        (b.order ?? Number.MAX_SAFE_INTEGER)
    );

const loadRiskyPositions = (token) => {
  if (!referentialRequest) {
    const request = new Promise((resolve, reject) =>
      ApiDataBase.getRiskyPositions({ token }, resolve, reject)
    ).then((response) => {
      loadedReferential = sortByOrder(response);
      return loadedReferential;
    });
    request.catch((err) => {
      console.warn(err);
      if (referentialRequest === request) referentialRequest = null;
    });
    referentialRequest = request;
  }
  return referentialRequest;
};

// `skip` spares the request to a list of changes that holds no risk factor.
export const useRiskyPositions = ({ skip = false } = {}) => {
  const [riskyPositions, setRiskyPositions] = useState(loadedReferential);
  const token = localStorage.getItem('xsrfToken');

  useEffect(() => {
    if (!token || skip) return undefined;
    let isMounted = true;
    loadRiskyPositions(token).then(
      (referential) => {
        if (isMounted) setRiskyPositions(referential);
      },
      () => {}
    );
    return () => {
      isMounted = false;
    };
  }, [token, skip]);

  return riskyPositions || [];
};

// While the referential is missing, a factor falls back on its BeSTT key.
const fallbackLabel = (besttField) =>
  besttField.replace(/^Penibilite_/, '').replace(/_/g, ' ');

export const formatRiskyPositions = (conditions, riskyPositions = []) => {
  if (!conditions?.position_at_risk) return templateText('format.riskNo');
  const selected = Object.keys(conditions.penibilite || {}).filter(
    (field) => conditions.penibilite[field] === true
  );
  if (selected.length === 0) return templateText('format.riskYes');
  const listed = riskyPositions.filter(({ besttField }) =>
    selected.includes(besttField)
  );
  const listedFields = listed.map(({ besttField }) => besttField);
  const labels = [
    ...listed.map(({ name }) => name),
    ...selected
      .filter((field) => !listedFields.includes(field))
      .map(fallbackLabel),
  ];
  return templateText('format.riskYesWith', { factors: labels.join(', ') });
};
